import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Button } from 'react-bootstrap';
import { RefreshCw } from 'lucide-react';
import { userService, companyService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import { useIsMobile } from '../hooks/useMediaQuery';
import SegmentedControl from '../components/SegmentedControl';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { useChartTheme } from '../components/chartTheme';
import { formatTime, plural } from '../utils/format';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Labels match the Logs page's windows
const TIMEFRAMES = [
  { label: '1h', period: 'hour', hours: 1, intervalMinutes: 5, intervals: 12 },
  { label: '4h', period: '4 hours', hours: 4, intervalMinutes: 20, intervals: 12 },
  { label: '8h', period: '8 hours', hours: 8, intervalMinutes: 40, intervals: 12 },
  { label: '24h', period: '24 hours', hours: 24, intervalMinutes: 120, intervals: 12 },
];
const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

const buildChartData = (apiData, countKey, tf) => {
  const now = new Date();
  const intervals = [];

  for (let i = tf.intervals - 1; i >= 0; i--) {
    const intervalStart = new Date(now.getTime() - i * tf.intervalMinutes * 60 * 1000);
    const hours = intervalStart.getHours().toString().padStart(2, '0');
    const minutes = intervalStart.getMinutes().toString().padStart(2, '0');
    intervals.push({
      label: `${hours}:${minutes}`,
      intervalIndex: tf.intervals - 1 - i,
      count: 0,
    });
  }

  if (apiData) {
    apiData.forEach((item) => {
      const idx = Number(item.intervalIndex);
      if (idx >= 0 && idx < intervals.length) {
        intervals[idx].count = Number(item[countKey]);
      }
    });
  }

  return intervals;
};

const total = (data) => data.reduce((sum, point) => sum + point.count, 0);

const Monitoring = () => {
  const ensureToken = useAuthToken();
  const isMobile = useIsMobile();
  const chartTheme = useChartTheme();
  // null until the first load, so the cards can tell "loading" from "nothing happened"
  const [signupData, setSignupData] = useState(null);
  const [whereaboutsData, setWhereaboutsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeframe, setTimeframe] = useState(TIMEFRAMES[0]);
  // Only the newest request may update the charts, so a slow reply for an earlier timeframe is dropped
  const requestId = useRef(0);

  const fetchData = useCallback(async (tf) => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      await ensureToken();

      const [signupsRes, whereaboutsRes] = await Promise.all([
        userService.getSignupsActivity(tf.hours, tf.intervalMinutes),
        companyService.getWhereaboutsActivity(tf.hours, tf.intervalMinutes),
      ]);
      if (id !== requestId.current) return;

      setSignupData(buildChartData(signupsRes.data, 'userCount', tf));
      setWhereaboutsData(buildChartData(whereaboutsRes.data, 'updateCount', tf));
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(serverMessage(err, 'Could not load the monitoring data.'));
      console.error(err);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [ensureToken]);

  // One fetch per timeframe change, then a refresh on a timer for as long as it is open
  useEffect(() => {
    fetchData(timeframe);
    const interval = setInterval(() => fetchData(timeframe), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData, timeframe]);

  // Phones: a shorter chart, with the side margins and Y axis handing their width to the bars
  const chartHeight = isMobile ? 260 : 400;
  const chartMargin = isMobile ? { top: 12, right: 8, left: 0, bottom: 0 } : { top: 20, right: 30, left: 20, bottom: 20 };
  const yAxisProps = isMobile ? { width: 32 } : {};

  const renderChart = (data, seriesName) => {
    if (error && !data) return <ErrorState message={error} onRetry={() => fetchData(timeframe)} inCard />;
    if (!data) return <LoadingState label="Loading" />;
    return (
      <div className="position-relative">
        {/* Old bars stay, dimmed, while a new timeframe loads */}
        {loading && (
          <div className="logs-overlay">
            <Spinner animation="border" size="sm" role="status">
              <span className="visually-hidden">Loading</span>
            </Spinner>
          </div>
        )}
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} margin={chartMargin}>
            <CartesianGrid {...chartTheme.grid} />
            <XAxis dataKey="label" {...chartTheme.xAxis} />
            <YAxis allowDecimals={false} {...chartTheme.yAxis} {...yAxisProps} />
            <Tooltip
              {...chartTheme.tooltip}
              formatter={(value) => [value, seriesName]}
              labelFormatter={(label) => `From ${label}`}
            />
            <Bar dataKey="count" fill={chartTheme.colors.emphasis} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const cardHeader = (title, data, singular) => (
    <Card.Header className="bg-light d-flex justify-content-between align-items-center gap-2">
      <h5 className="mb-0">{title}</h5>
      {data && <span className="small text-muted">{plural(total(data), singular)}</span>}
    </Card.Header>
  );

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Monitoring</h1>
          <p className="text-muted mb-0">
            Activity over the last {timeframe.period}, in {timeframe.intervalMinutes}-minute intervals.
            {lastUpdated && ` Updated ${formatTime(lastUpdated)}.`}
          </p>
        </Col>
      </Row>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
        <SegmentedControl
          options={TIMEFRAMES.map((tf) => ({ value: tf.label, label: tf.label }))}
          value={timeframe.label}
          onChange={(label) => setTimeframe(TIMEFRAMES.find((tf) => tf.label === label))}
          size={isMobile ? undefined : 'sm'}
          ariaLabel="Time window"
        />
        <Button
          variant="outline-secondary"
          size={isMobile ? undefined : 'sm'}
          className="d-inline-flex align-items-center"
          onClick={() => fetchData(timeframe)}
          disabled={loading}
        >
          <RefreshCw size={14} className="me-1" aria-hidden="true" />Refresh
        </Button>
      </div>

      {/* Once there are charts, a failed refresh keeps them and says so here */}
      {error && signupData && <ErrorState message={error} onRetry={() => fetchData(timeframe)} className="mb-4" />}

      <Row className="gy-3 gy-md-0">
        <Col md={6}>
          <Card className="shadow-sm">
            {cardHeader('User sign-ups', signupData, 'sign-up')}
            <Card.Body>
              {renderChart(signupData, 'Sign-ups')}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            {cardHeader('Whereabouts changes', whereaboutsData, 'change')}
            <Card.Body>
              {renderChart(whereaboutsData, 'Changes')}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Monitoring;
